import { useCallback, useSyncExternalStore } from 'react';

import type { LibraryInstallResult } from '@/lib/api/generated/model';

import { getInstallReport, subscribeToInstallReports } from '../installReports';
import type { ItemRef } from '../searchParams';

/**
 * The stored report for one item, or nothing (spec 108 FR-005).
 *
 * `useSyncExternalStore` rather than reading the store during render. The store
 * is mutable module state, and reading mutable state in render is how a
 * concurrent renderer ends up showing two different answers in one commit. The
 * subscription is also what makes a write from the install mutation reach a
 * drawer that is already open, without the page threading the result through by
 * hand.
 *
 * The snapshot is the stored object itself, which is replaced rather than
 * mutated on every write, so reference identity is a correct answer to "has this
 * changed" and `undefined` is stable for an item with no report.
 */
export function useInstallReport(
  workspaceId: string,
  ref: ItemRef | undefined,
): LibraryInstallResult | undefined {
  const snapshot = useCallback(
    () => getInstallReport(workspaceId, ref),
    [workspaceId, ref?.kind, ref?.key], // eslint-disable-line react-hooks/exhaustive-deps -- the ref object is rebuilt on every render; its two fields are the identity
  );
  return useSyncExternalStore(subscribeToInstallReports, snapshot, snapshot);
}
